import { useEffect, useRef, useState, type FormEvent } from 'react'
import { AdminPageShell } from '../admin/AdminPageShell'
import type { AdminSession } from '../admin/AdminShell'
import { resolveApiBaseUrl } from '../../lib/api-base-url'
import { useEscapeClose } from '../../lib/use-escape-close'

type DetailActivityEvent = {
  collaboratorId?: string | null
  collaboratorName: string
  date: string
  time: string
  space: string
  type: string
  details: string
  device?: string
  accuracy?: string
  method?: string
  journeyId?: string
  journeyIndex?: number
  punctualityStatus?: 'on_time' | 'within_tolerance' | 'late' | 'absent_after_tolerance' | 'not_applicable'
  minutesLate?: number
  expectedTime?: string | null
}

type DetailActivitiesData = {
  stats: {
    onTimeArrivals: number
    delays: number
    absences: number
    toleranceAlerts?: number
    suspectedFraud: number
    overtimeHours: number
    tolerance: number
    totalPunches: number
    totalCollaborators: number
  }
  events: DetailActivityEvent[]
  analytics: {
    settings: {
      delayToleranceMinutes: number
      standardWorkdayMinutes: number
      defaultChartType: 'bar' | 'line'
      defaultViewMode: 'journeys' | 'day'
    }
    totals: {
      onTime: number
      delays: number
      absences: number
      fraudAttempts: number
      overtimeHours: number
    }
    timeline: Array<{
      date: string
      onTime: number
      delays: number
      absences: number
      fraudAttempts: number
      overtimeHours: number
    }>
  }
}

type SpaceOption = {
  id: string
  name: string
}

type SpacesPayload = {
  items: SpaceOption[]
}

type CollaboratorProfile = {
  id: string
  name: string
  email: string
  roleSlug: string
  status: string
  maxDailyJourneys: number
}

type CollaboratorProfileForm = {
  name: string
  email: string
  status: string
  maxDailyJourneys: number
}

type JourneySummary = {
  collaboratorName: string
  date: string
  journeyLabel: string
  space: string
  startedAt: string
  endedAt: string
  workedMinutes: number
  events: DetailActivityEvent[]
}

type CollaboratorWorkSummary = {
  collaboratorName: string
  dayMinutes: number
  weekMinutes: number
  monthMinutes: number
  journeysCount: number
  spacesCount: number
  journeys: JourneySummary[]
}

const apiBaseUrl = resolveApiBaseUrl()

function formatInputDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getDefaultDateRange() {
  const end = new Date()
  const start = new Date(end)
  start.setDate(end.getDate() - 6)

  return {
    startDate: formatInputDate(start),
    endDate: formatInputDate(end),
  }
}

function parseActivityDateTime(event: DetailActivityEvent) {
  return new Date(`${event.date}T${event.time}.000Z`)
}

function formatDuration(minutes: number) {
  const safeMinutes = Math.max(0, Math.round(minutes))
  const hours = Math.floor(safeMinutes / 60)
  const remainingMinutes = safeMinutes % 60

  return `${hours}h ${String(remainingMinutes).padStart(2, '0')}min`
}

function startOfWeek(date: Date) {
  const weekStart = new Date(date)
  const day = weekStart.getDay()
  const diff = day === 0 ? -6 : 1 - day
  weekStart.setDate(weekStart.getDate() + diff)
  weekStart.setHours(0, 0, 0, 0)
  return weekStart
}

function isSameDay(value: Date, reference: Date) {
  return value.getFullYear() === reference.getFullYear() &&
    value.getMonth() === reference.getMonth() &&
    value.getDate() === reference.getDate()
}

function isSameMonth(value: Date, reference: Date) {
  return value.getFullYear() === reference.getFullYear() && value.getMonth() === reference.getMonth()
}

function isSameWeek(value: Date, reference: Date) {
  const weekStart = startOfWeek(reference)
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 7)
  return value >= weekStart && value < weekEnd
}

function calculateJourneyWorkedMinutes(events: DetailActivityEvent[]) {
  const orderedEvents = [...events].sort((left, right) =>
    parseActivityDateTime(left).getTime() - parseActivityDateTime(right).getTime(),
  )
  const checkin = orderedEvents.find((event) => event.type === 'checkin' || event.type === 'delay')
  const checkout = [...orderedEvents].reverse().find((event) => event.type === 'checkout')

  if (!checkin || !checkout) {
    return 0
  }

  const checkinTime = parseActivityDateTime(checkin).getTime()
  const checkoutTime = parseActivityDateTime(checkout).getTime()
  let breakMinutes = 0
  let breakStart: DetailActivityEvent | null = null

  for (const event of orderedEvents) {
    if (event.type === 'break_start') {
      breakStart = event
      continue
    }

    if (event.type === 'break_end' && breakStart) {
      breakMinutes += Math.max(
        0,
        (parseActivityDateTime(event).getTime() - parseActivityDateTime(breakStart).getTime()) / 60000,
      )
      breakStart = null
    }
  }

  return Math.max(0, (checkoutTime - checkinTime) / 60000 - breakMinutes)
}

function TruncatedCell({ value, className }: { value?: string; className?: string }) {
  const displayValue = value && value.trim() ? value : 'N/A'

  return (
    <span className={`admin-table__truncate${className ? ` ${className}` : ''}`} title={displayValue}>
      {displayValue}
    </span>
  )
}

export function DetailsPage({ session }: { session: AdminSession }) {
  const defaultDateRange = getDefaultDateRange()
  const [data, setData] = useState<DetailActivitiesData | null>(null)
  const [spaces, setSpaces] = useState<SpaceOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingProfile, setEditingProfile] = useState<CollaboratorProfile | null>(null)
  const [profileForm, setProfileForm] = useState<CollaboratorProfileForm>({
    name: '',
    email: '',
    status: 'active',
    maxDailyJourneys: 1,
  })
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileFeedback, setProfileFeedback] = useState<string | null>(null)
  const [selectedWorkSummary, setSelectedWorkSummary] = useState<CollaboratorWorkSummary | null>(null)
  const [selectedJourneyEvents, setSelectedJourneyEvents] = useState<JourneySummary | null>(null)

  // Filters
  const [startDate, setStartDate] = useState(defaultDateRange.startDate)
  const [endDate, setEndDate] = useState(defaultDateRange.endDate)
  const [selectedSpace, setSelectedSpace] = useState('all')
  const [selectedEvent, setSelectedEvent] = useState('all')
  const [collaboratorName, setCollaboratorName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'journeys' | 'day'>('journeys')

  // Pagination
  const [page, setPage] = useState(1)
  const [summaryPage, setSummaryPage] = useState(1)
  const [journeyPage, setJourneyPage] = useState(1)
  const [journeyFilter, setJourneyFilter] = useState('')
  const pageSize = 10

  // Preferences
  const [chartType, setChartType] = useState<'bar' | 'line'>('line')
  const settingsAppliedRef = useRef(false)

  const token = session.session.token

  const fetchDetails = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        spaceName: selectedSpace,
        eventType: selectedEvent,
        collaboratorName,
        query: searchQuery,
        viewMode,
      })
      const response = await fetch(`${apiBaseUrl}/activities/details?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (!response.ok) {
        throw new Error('Falha ao obter detalhamento de atividades.')
      }
      const payload = (await response.json()) as DetailActivitiesData
      setData(payload)
      if (!settingsAppliedRef.current) {
        setViewMode(payload.analytics.settings.defaultViewMode)
        settingsAppliedRef.current = true
      }
      setPage(1) // Reset to first page on new fetch
    } catch (err: any) {
      setError(err.message || 'Erro desconhecido')
    } finally {
      setLoading(false)
    }
  }

  const fetchSpaces = async () => {
    try {
      const params = new URLSearchParams({
        scope: 'active',
        sortBy: 'name',
        sortDir: 'asc',
      })
      const response = await fetch(`${apiBaseUrl}/spaces?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Falha ao obter espaços.')
      }

      const payload = (await response.json()) as SpacesPayload
      setSpaces(payload.items || [])
    } catch {
      setSpaces([])
    }
  }

  useEffect(() => {
    void fetchSpaces()
  }, [token])

  useEffect(() => {
    void fetchDetails()
  }, [startDate, endDate, selectedSpace, selectedEvent, viewMode]) // Auto-fetch on major filters

  const handleApplyAdvancedFilters = (e: FormEvent) => {
    e.preventDefault()
    void fetchDetails()
  }

  const handleClearFilters = () => {
    const nextDateRange = getDefaultDateRange()
    setStartDate(nextDateRange.startDate)
    setEndDate(nextDateRange.endDate)
    setSelectedSpace('all')
    setSelectedEvent('all')
    setCollaboratorName('')
    setSearchQuery('')
    setViewMode('journeys')
    setPage(1)
  }

  const handleExportCSV = () => {
    if (!data || data.events.length === 0) return

    const headers = ['Data', 'Horário', 'Colaborador', 'Espaço', 'Jornada', 'Evento', 'Dispositivo', 'Acurácia', 'Método', 'Detalhes']
    const rows = data.events.map((evt) => [
      evt.date,
      evt.time,
      evt.collaboratorName,
      evt.space,
      evt.journeyIndex ? `Jornada ${evt.journeyIndex}` : 'Dia consolidado',
      evt.type,
      getDeviceOsLabel(evt.device),
      evt.accuracy || 'N/A',
      evt.method || 'N/A',
      evt.details
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((val) => `"${val.replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `auditoria_presenca_${startDate}_a_${endDate}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const closeProfileOverlay = () => {
    setEditingProfile(null)
    setProfileFeedback(null)
  }

  const closeWorkSummaryModal = () => {
    setSelectedWorkSummary(null)
    setJourneyPage(1)
    setJourneyFilter('')
  }

  const closeJourneyEventsModal = () => {
    setSelectedJourneyEvents(null)
  }

  useEscapeClose(Boolean(editingProfile || selectedWorkSummary || selectedJourneyEvents), () => {
    if (selectedJourneyEvents) {
      closeJourneyEventsModal()
      return
    }

    if (selectedWorkSummary) {
      closeWorkSummaryModal()
      return
    }

    closeProfileOverlay()
  })

  const handleEditCollaboratorProfile = async (collaboratorId: string) => {
    setProfileFeedback(null)

    try {
      const response = await fetch(`${apiBaseUrl}/collaborators/${collaboratorId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error('Falha ao carregar perfil do colaborador.')
      }

      const payload = (await response.json()) as { collaborator: CollaboratorProfile }
      setEditingProfile(payload.collaborator)
      setProfileForm({
        name: payload.collaborator.name,
        email: payload.collaborator.email,
        status: payload.collaborator.status,
        maxDailyJourneys: Number(payload.collaborator.maxDailyJourneys) || 1,
      })
    } catch (err) {
      setProfileFeedback(err instanceof Error ? err.message : 'Falha ao carregar perfil.')
    }
  }

  const handleSaveCollaboratorProfile = async () => {
    if (!editingProfile) {
      return
    }

    setProfileSaving(true)
    setProfileFeedback(null)

    try {
      const response = await fetch(`${apiBaseUrl}/collaborators/${editingProfile.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: profileForm.name,
          email: profileForm.email,
          roleSlug: editingProfile.roleSlug,
          status: profileForm.status,
          maxDailyJourneys: Math.max(1, Math.floor(Number(profileForm.maxDailyJourneys) || 1)),
        }),
      })

      const payload = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error(payload?.message || 'Falha ao salvar perfil do colaborador.')
      }

      const collaborator = payload.collaborator as CollaboratorProfile
      setEditingProfile(collaborator)
      setProfileForm({
        name: collaborator.name,
        email: collaborator.email,
        status: collaborator.status,
        maxDailyJourneys: Number(collaborator.maxDailyJourneys) || 1,
      })
      setProfileFeedback('Perfil atualizado.')
      void fetchDetails()
    } catch (err) {
      setProfileFeedback(err instanceof Error ? err.message : 'Falha ao salvar perfil.')
    } finally {
      setProfileSaving(false)
    }
  }

  const getEventBadgeStyle = (type: string) => {
    switch (type) {
      case 'checkin':
        return { backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.2)' }
      case 'checkout':
        return { backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.2)' }
      case 'break_start':
      case 'break_end':
        return { backgroundColor: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', border: '1px solid rgba(124, 58, 237, 0.2)' }
      case 'delay':
        return { backgroundColor: 'rgba(217, 119, 6, 0.1)', color: '#d97706', border: '1px solid rgba(217, 119, 6, 0.2)' }
      case 'absence':
        return { backgroundColor: 'rgba(239, 68, 68, 0.12)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.28)' }
      case 'unauthorized':
      case 'spoof_attempt':
        return { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)' }
      case 'journey_authorized':
        return { backgroundColor: 'rgba(14, 165, 233, 0.1)', color: '#0284c7', border: '1px solid rgba(14, 165, 233, 0.2)' }
      case 'day_summary':
        return { backgroundColor: 'rgba(15, 23, 42, 0.08)', color: 'var(--text)', border: '1px solid var(--line)' }
      default:
        return { backgroundColor: 'var(--bg-alt)', color: 'var(--text)', border: '1px solid var(--line)' }
    }
  }

  const getEventLabel = (type: string) => {
    switch (type) {
      case 'checkin':
        return 'Entrada'
      case 'checkout':
        return 'Saída'
      case 'break_start':
        return 'Intervalo (Início)'
      case 'break_end':
        return 'Intervalo (Fim)'
      case 'delay':
        return 'Atraso'
      case 'absence':
        return 'Falta'
      case 'unauthorized':
        return 'Não Autorizado'
      case 'spoof_attempt':
        return 'Alerta de GPS'
      case 'journey_authorized':
        return 'Nova jornada liberada'
      case 'day_summary':
        return 'Dia consolidado'
      default:
        return type
    }
  }

  const eventsList = data?.events || []
  const sortedEventDates = eventsList
    .map((event) => event.date)
    .sort()
  const latestEventDate = sortedEventDates[sortedEventDates.length - 1]
  const referenceDate = new Date(`${latestEventDate || endDate}T12:00:00.000Z`)
  const journeyGroups = new Map<string, DetailActivityEvent[]>()

  eventsList
    .filter((event) => event.type !== 'day_summary' && event.type !== 'absence')
    .forEach((event) => {
      const key = event.journeyId
        ? `${event.journeyId}:${event.space}`
        : `${event.collaboratorName}:${event.date}:${event.space}:${event.journeyIndex || 1}`
      journeyGroups.set(key, [...(journeyGroups.get(key) || []), event])
    })

  const journeySummariesRaw = Array.from(journeyGroups.values()).map((events) => {
    const orderedEvents = [...events].sort((left, right) =>
      parseActivityDateTime(left).getTime() - parseActivityDateTime(right).getTime(),
    )
    const firstEvent = orderedEvents[0]
    const lastEvent = orderedEvents[orderedEvents.length - 1]

    return {
      collaboratorName: firstEvent.collaboratorName,
      date: firstEvent.date,
      space: firstEvent.space,
      startedAt: firstEvent.time,
      endedAt: lastEvent.time,
      workedMinutes: calculateJourneyWorkedMinutes(orderedEvents),
      events: orderedEvents,
      journeyLabel: '',
      journeyIndex: firstEvent.journeyIndex || 1,
      firstEventTime: parseActivityDateTime(firstEvent).getTime()
    }
  })

  // Group raw summaries by collaboratorName + date + space to calculate local journey indexes
  const summariesBySpace = new Map<string, typeof journeySummariesRaw>()

  journeySummariesRaw.forEach((summary) => {
    const key = `${summary.collaboratorName}:${summary.date}:${summary.space}`
    summariesBySpace.set(key, [...(summariesBySpace.get(key) || []), summary])
  })

  const journeySummaries = Array.from(summariesBySpace.values()).flatMap((summaries) => {
    const sorted = [...summaries].sort((left, right) => left.firstEventTime - right.firstEventTime)
    return sorted.map((summary, index) => {
      return {
        collaboratorName: summary.collaboratorName,
        date: summary.date,
        journeyLabel: `Jornada ${index + 1}`,
        space: summary.space,
        startedAt: summary.startedAt,
        endedAt: summary.endedAt,
        workedMinutes: summary.workedMinutes,
        events: summary.events,
      } satisfies JourneySummary
    })
  })

  const workSummaries = Array.from(
    journeySummaries.reduce((summaryMap, journey) => {
      const current = summaryMap.get(journey.collaboratorName) || {
        collaboratorName: journey.collaboratorName,
        dayMinutes: 0,
        weekMinutes: 0,
        monthMinutes: 0,
        journeysCount: 0,
        spacesCount: 0,
        journeys: [],
      }
      const journeyDate = new Date(`${journey.date}T12:00:00.000Z`)
      const journeySpaces = new Set(
        current.journeys.flatMap((item) => item.events.map((event) => event.space)),
      )

      journey.events.forEach((event) => journeySpaces.add(event.space))

      current.journeysCount += 1
      current.spacesCount = journeySpaces.size
      current.journeys.push(journey)

      if (isSameDay(journeyDate, referenceDate)) {
        current.dayMinutes += journey.workedMinutes
      }

      if (isSameWeek(journeyDate, referenceDate)) {
        current.weekMinutes += journey.workedMinutes
      }

      if (isSameMonth(journeyDate, referenceDate)) {
        current.monthMinutes += journey.workedMinutes
      }

      summaryMap.set(journey.collaboratorName, current)
      return summaryMap
    }, new Map<string, CollaboratorWorkSummary>()).values(),
  ).sort((left, right) => left.collaboratorName.localeCompare(right.collaboratorName))
  const summaryTotalPages = Math.max(1, Math.ceil(workSummaries.length / pageSize))
  const safeSummaryPage = Math.min(summaryPage, summaryTotalPages)
  const visibleWorkSummaries = workSummaries.slice(
    (safeSummaryPage - 1) * pageSize,
    safeSummaryPage * pageSize,
  )
  const selectedJourneys = selectedWorkSummary?.journeys || []
  const filteredSelectedJourneys = selectedJourneys.filter((j) => {
    if (!journeyFilter) return true
    const search = journeyFilter.toLowerCase()
    return (
      j.space.toLowerCase().includes(search) ||
      j.journeyLabel.toLowerCase().includes(search) ||
      j.date.toLowerCase().includes(search)
    )
  })
  const journeyTotalPages = Math.max(1, Math.ceil(filteredSelectedJourneys.length / pageSize))
  const safeJourneyPage = Math.min(journeyPage, journeyTotalPages)
  const visibleSelectedJourneys = filteredSelectedJourneys.slice(
    (safeJourneyPage - 1) * pageSize,
    safeJourneyPage * pageSize,
  )

  // ----------------------------------------------------
  // Statistics Calculations for DetailsPage Chart
  // ----------------------------------------------------
  const calculateDetailsPageStats = () => {
    const analytics = data?.analytics
    const totals = analytics?.totals || { onTime: 0, delays: 0, absences: 0, fraudAttempts: 0, overtimeHours: 0 }
    const timeline = analytics?.timeline || []
    const tolerance = analytics?.settings.delayToleranceMinutes || 0
    const maxValue = Math.max(tolerance, ...timeline.flatMap((point) => [point.onTime, point.delays, point.absences, point.fraudAttempts, point.overtimeHours]), 1)
    const x = (index: number) => timeline.length <= 1 ? 250 : 50 + (index * 400) / (timeline.length - 1)
    const y = (value: number) => 150 - (value / maxValue) * 130
    const series = [
      { key: 'delays', label: 'Atrasos', color: '#f59e0b' },
      { key: 'absences', label: 'Faltas', color: '#ef4444' },
      { key: 'fraudAttempts', label: 'Tentativas de fraude', color: '#ec4899' },
      { key: 'overtimeHours', label: 'Horas extras', color: '#7c3aed' },
    ] as const

    return {
      onTimeArrivals: totals.onTime,
      delays: totals.delays,
      absences: totals.absences,
      suspectedFraud: totals.fraudAttempts,
      overtimeHours: totals.overtimeHours,
      totalPunches: totals.onTime + totals.delays,
      totalCollaborators: 0,
      toleranceAlerts: data?.stats.toleranceAlerts || 0,
      tolerance,
      toleranceY: y(tolerance),
      timeline,
      x,
      y,
      series,
      onTimeHeight: y(0) - y(totals.onTime),
      delayHeight: y(0) - y(totals.delays),
      absenceHeight: y(0) - y(totals.absences),
      suspectHeight: y(0) - y(totals.fraudAttempts),
      overtimeHeight: y(0) - y(totals.overtimeHours),
      toleranceHeight: y(0) - y(tolerance),
      points: timeline.map((point, index) => ({
        x: x(index),
        y: y(point.delays),
        label: new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(new Date(`${point.date}T12:00:00Z`)),
        value: point.delays,
        color: '#f59e0b',
      })),
      pathD: timeline.length ? `M ${timeline.map((point, index) => `${x(index)},${y(point.delays)}`).join(' L ')}` : '',
      areaD: timeline.length ? `M ${timeline.map((point, index) => `${x(index)},${y(point.delays)}`).join(' L ')} L ${x(timeline.length - 1)},150 L ${x(0)},150 Z` : '',
    }
  }

  const chartStats = calculateDetailsPageStats()
  const totalPages = Math.max(1, Math.ceil(eventsList.length / pageSize))
  const paginatedEvents = eventsList.slice((page - 1) * pageSize, page * pageSize)
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1)
  const summaryPageNumbers = Array.from({ length: summaryTotalPages }, (_, index) => index + 1)
  const journeyPageNumbers = Array.from({ length: journeyTotalPages }, (_, index) => index + 1)

  useEffect(() => {
    setSummaryPage((current) => Math.min(Math.max(current, 1), summaryTotalPages))
  }, [summaryTotalPages])

  useEffect(() => {
    setJourneyPage((current) => Math.min(Math.max(current, 1), journeyTotalPages))
  }, [journeyTotalPages])

  return (
    <AdminPageShell title="Auditoria e Detalhes de Atividade" icon={<HistoryHeaderIcon />}>
      <div className="admin-dashboard">
        
        {/* Filtros Avançados */}
        <div className="admin-panel" style={{ padding: '20px', backgroundColor: 'var(--surface)', marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 16px', fontSize: '0.95rem' }}>Parâmetros de Pesquisa e Filtros Avançados</h4>
          <form onSubmit={handleApplyAdvancedFilters}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', marginBottom: '6px' }}>Data Inicial</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '5px', fontSize: '0.85rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', marginBottom: '6px' }}>Data Final</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '5px', fontSize: '0.85rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', marginBottom: '6px' }}>Espaço</label>
                <select
                  value={selectedSpace}
                  onChange={(e) => setSelectedSpace(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '5px', fontSize: '0.85rem', backgroundColor: '#fff' }}
                >
                  <option value="all">Todos os espaços</option>
                  {spaces.map((space) => (
                    <option key={space.id} value={space.name}>{space.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', marginBottom: '6px' }}>Evento</label>
                <select
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '5px', fontSize: '0.85rem', backgroundColor: '#fff' }}
                >
                  <option value="all">Todos os eventos</option>
                  <option value="entry_exit">Entradas & Saídas</option>
                  <option value="breaks">Intervalos</option>
                  <option value="alerts">Alertas & Suspeitos</option>
                  <option value="checkin">Entradas normais</option>
                  <option value="checkout">Saídas normais</option>
                  <option value="delay">Atrasos</option>
                  <option value="spoof_attempt">Alertas de Fraude GPS</option>
                  <option value="unauthorized">Marcações Não Autorizadas</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', marginBottom: '6px' }}>Visualização</label>
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as 'journeys' | 'day')}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '5px', fontSize: '0.85rem', backgroundColor: '#fff' }}
                >
                  <option value="journeys">Separar por jornada</option>
                  <option value="day">Consolidar como dia único</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', marginBottom: '6px' }}>Colaborador</label>
                <input
                  type="text"
                  placeholder="Nome do colaborador..."
                  value={collaboratorName}
                  onChange={(e) => setCollaboratorName(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '5px', fontSize: '0.85rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', marginBottom: '6px' }}>Busca Avançada</label>
                <input
                  type="text"
                  placeholder="Dispositivo, método, log..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '5px', fontSize: '0.85rem' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="admin-action-button"
                style={{ backgroundColor: 'transparent', border: '1px solid var(--line)', color: 'var(--text)' }}
                onClick={handleClearFilters}
              >
                Limpar Filtros
              </button>
              <button
                type="button"
                className="admin-action-button"
                style={{ backgroundColor: 'var(--brand-purple)', color: '#fff' }}
                onClick={handleExportCSV}
                disabled={!data || data.events.length === 0}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '13px', height: '13px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Exportar CSV
              </button>
              <button
                type="submit"
                className="admin-action-button"
                style={{ backgroundColor: 'var(--brand-purple)', color: '#fff' }}
              >
                Aplicar Filtros
              </button>
            </div>
          </form>
        </div>

        {/* Gráfico de Assiduidade e Horas Extras */}
        <div className="admin-panel" style={{ padding: '24px', backgroundColor: 'var(--surface)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Gráfico de Assiduidade e Horas Extras (Período Selecionado)</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '1px', border: '1px solid var(--line)', borderRadius: '6px', overflow: 'hidden', backgroundColor: 'var(--bg-alt)' }}>
                <button
                  type="button"
                  onClick={() => setChartType('bar')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.74rem',
                    fontWeight: 600,
                    border: '0',
                    backgroundColor: chartType === 'bar' ? 'var(--brand-purple)' : 'transparent',
                    color: chartType === 'bar' ? '#fff' : 'var(--muted)',
                    cursor: 'pointer'
                  }}
                >
                  Linha temporal
                </button>
                <button
                  type="button"
                  onClick={() => setChartType('line')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.74rem',
                    fontWeight: 600,
                    border: '0',
                    backgroundColor: chartType === 'line' ? 'var(--brand-purple)' : 'transparent',
                    color: chartType === 'line' ? '#fff' : 'var(--muted)',
                    cursor: 'pointer'
                  }}
                >
                  Linha
                </button>
              </div>
              <span style={{ fontSize: '0.78rem', padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--bg-alt)', color: 'var(--muted)', fontWeight: 500, border: '1px solid var(--line)' }}>
                Tolerância: {chartStats.tolerance} min
              </span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'center' }}>
            {chartType === 'bar' ? (
              /* Gráfico de Barras SVG/CSS */
              <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: '180px', padding: '16px 8px 8px', borderBottom: '2px solid var(--line)', position: 'relative' }}>
                {/* Chegadas no Horário */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${chartStats.onTimeHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#10b981', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>No Horário</span>
                </div>

                {/* Atrasos */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${chartStats.delayHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#f59e0b', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>Atrasos</span>
                </div>

                {/* Faltas */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${chartStats.absenceHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#ef4444', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>Faltas</span>
                </div>

                {/* Suspeitas */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${chartStats.suspectHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#ec4899', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(236, 72, 153, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>Suspeitas</span>
                </div>

                {/* Horas Extras */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${chartStats.overtimeHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#7c3aed', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>H. Extras</span>
                </div>

                {/* Tolerância */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${chartStats.toleranceHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#3b82f6', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>Tolerância</span>
                </div>
              </div>
            ) : (
              /* Gráfico de Linha / Área SVG */
              <div style={{ height: '180px', padding: '16px 8px 8px', borderBottom: '2px solid var(--line)', position: 'relative' }}>
                <svg viewBox="0 0 500 180" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="detailsChartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--brand-purple)" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="var(--brand-purple)" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>
                  
                  {/* Grid Lines */}
                  <line x1="40" y1="150" x2="440" y2="150" stroke="var(--line)" strokeWidth="1" strokeDasharray="4 4" />
                  <line x1="40" y1="80" x2="440" y2="80" stroke="var(--line)" strokeWidth="1" strokeDasharray="4 4" />
                  <line x1="40" y1="10" x2="440" y2="10" stroke="var(--line)" strokeWidth="1" strokeDasharray="4 4" />

                  <line x1="50" y1={chartStats.toleranceY} x2="450" y2={chartStats.toleranceY} stroke="#0ea5e9" strokeWidth="2" strokeDasharray="7 4" />
                  <text x="450" y={Math.max(12, chartStats.toleranceY - 5)} textAnchor="end" fontSize="10" fontWeight="700" fill="#0284c7">
                    Tolerância {chartStats.tolerance} min
                  </text>

                  {/* Filled Area */}
                  <path d={chartStats.areaD} fill="url(#detailsChartGradient)" />

                  {/* Smooth Line */}
                  <path d={chartStats.pathD} fill="none" stroke="var(--brand-purple)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

                  {/* Points circles and labels */}
                  {chartStats.points.map((p, i) => (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r="6" fill="#fff" stroke={p.color} strokeWidth="3" style={{ cursor: 'pointer', transition: 'all 0.2s' }} />
                      <text x={p.x} y={p.y - 12} textAnchor="middle" fontSize="10" fontWeight="bold" fill="var(--text)">
                        {p.value}
                      </text>
                      <text x={p.x} y="170" textAnchor="middle" fontSize="10" fontWeight="600" fill="var(--muted)">
                        {p.label}
                      </text>
                    </g>
                  ))}
                </svg>
              </div>
            )}

            {/* Cartões de Métricas Detalhadas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px' }}>
              {/* No Horário */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Chegadas no Horário</div>
                <strong style={{ fontSize: '1.15rem', color: '#10b981' }}>{chartStats.onTimeArrivals}</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${chartStats.totalPunches > 0 ? (chartStats.onTimeArrivals / chartStats.totalPunches) * 100 : 0}%`, backgroundColor: '#10b981' }} />
                </div>
              </div>

              {/* Atrasos */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Atrasos</div>
                <strong style={{ fontSize: '1.15rem', color: '#f59e0b' }}>{chartStats.delays}</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${chartStats.totalPunches > 0 ? (chartStats.delays / chartStats.totalPunches) * 100 : 0}%`, backgroundColor: '#f59e0b' }} />
                </div>
              </div>

              {/* Faltas */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Ausências / Faltas</div>
                <strong style={{ fontSize: '1.15rem', color: '#ef4444' }}>{chartStats.absences}</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${chartStats.totalCollaborators > 0 ? (chartStats.absences / (chartStats.totalCollaborators * 5)) * 100 : 0}%`, backgroundColor: '#ef4444' }} />
                </div>
              </div>

              {/* Suspeitas */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Suspeitas / Alertas</div>
                <strong style={{ fontSize: '1.15rem', color: '#ec4899' }}>{chartStats.suspectedFraud}</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(236, 72, 153, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${chartStats.totalPunches > 0 ? (chartStats.suspectedFraud / chartStats.totalPunches) * 100 : 0}%`, backgroundColor: '#ec4899' }} />
                </div>
              </div>

              {/* Horas Extras */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Horas Extras</div>
                <strong style={{ fontSize: '1.15rem', color: '#7c3aed' }}>{chartStats.overtimeHours}h</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(124, 58, 237, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${chartStats.overtimeHours > 0 ? Math.min(100, (chartStats.overtimeHours / 8) * 100) : 0}%`, backgroundColor: '#7c3aed' }} />
                </div>
              </div>

              {/* Tolerância */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Tolerância Padrão</div>
                <strong style={{ fontSize: '1.15rem', color: '#3b82f6' }}>{chartStats.tolerance}m</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: '100%', backgroundColor: '#3b82f6' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Listagem de Auditoria */}
        <div className="admin-panel admin-panel--table-card" style={{ padding: '24px', backgroundColor: 'var(--surface)' }}>
          <div className="admin-panel__header" style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: 0 }}>Histórico Consolidado de Marcações</h3>
            <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: '0.82rem' }}>Dados completos obtidos do banco de dados, incluindo telemetria dos aparelhos dos colaboradores</p>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)' }}>
              Carregando dados de auditoria...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '36px', color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.05)', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.2)' }}>
              {error}
            </div>
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table admin-table--details">
                  <thead>
                    <tr>
                      <th className="admin-table__date">Data</th>
                      <th className="admin-table__time">Horário</th>
                      <th style={{ width: '180px' }}>Colaborador</th>
                      <th style={{ width: '220px' }}>Espaço</th>
                      <th style={{ width: '120px' }}>Jornada</th>
                      <th style={{ width: '150px' }}>Evento</th>
                      <th className="admin-table__device">Dispositivo</th>
                      <th className="admin-table__accuracy">Acurácia</th>
                      <th className="admin-table__method">Método</th>
                      <th className="admin-table__operational-details">Detalhes operacionais</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEvents.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ textAlign: 'center', padding: '36px 0' }}>
                          <div className="admin-empty-state">
                            <strong>Nenhuma atividade encontrada</strong>
                            <p>Nenhum registro corresponde aos filtros selecionados.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedEvents.map((evt, idx) => (
                        <tr
                          key={`${evt.collaboratorName}-${evt.date}-${evt.time}-${idx}`}
                          style={{
                            backgroundColor: evt.type === 'spoof_attempt' || evt.type === 'absence' || evt.type === 'delay'
                              ? 'rgba(239, 68, 68, 0.04)'
                              : evt.punctualityStatus === 'within_tolerance'
                                ? 'rgba(249, 115, 22, 0.07)'
                                : undefined,
                            borderLeft: evt.type === 'spoof_attempt' || evt.type === 'absence' || evt.type === 'delay'
                              ? '3px solid #ef4444'
                              : evt.punctualityStatus === 'within_tolerance'
                                ? '3px solid #f97316'
                                : undefined
                          }}
                        >
                          <td className="admin-table__date">{evt.date}</td>
                          <td className="admin-table__time"><strong>{evt.time}</strong></td>
                          <td>
                            <div style={{ display: 'grid', gap: '4px' }}>
                              {evt.collaboratorId ? (
                                <button
                                  type="button"
                                  className="admin-collaborator-link"
                                  onClick={() => void handleEditCollaboratorProfile(evt.collaboratorId as string)}
                                >
                                  {evt.collaboratorName}
                                </button>
                              ) : (
                                <strong>{evt.collaboratorName}</strong>
                              )}
                            </div>
                          </td>
                          <td>{evt.space}</td>
                          <td>{evt.journeyIndex ? `Jornada ${evt.journeyIndex}` : 'Dia'}</td>
                          <td>
                            <span
                              style={{
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                display: 'inline-block',
                                ...getEventBadgeStyle(evt.type)
                              }}
                            >
                              {getEventLabel(evt.type)}
                            </span>
                          </td>
                          <td className="admin-table__device">
                            <TruncatedCell value={getDeviceOsLabel(evt.device)} />
                          </td>
                          <td className="admin-table__accuracy">
                            <span style={{ color: (evt.accuracy && parseInt(evt.accuracy) > 20) ? '#d97706' : 'inherit' }}>
                              {evt.accuracy || 'N/A'}
                            </span>
                          </td>
                          <td className="admin-table__method">
                            <TruncatedCell value={evt.method} className="admin-table__truncate--muted" />
                          </td>
                          <td className="admin-table__operational-details" style={{
                            color: evt.type === 'absence' || evt.type === 'delay'
                              ? '#b91c1c'
                              : evt.punctualityStatus === 'within_tolerance'
                                ? '#c2410c'
                                : undefined,
                            fontWeight: evt.type === 'absence' || evt.type === 'delay' || evt.punctualityStatus === 'within_tolerance' ? 700 : undefined,
                          }}>
                            <TruncatedCell value={evt.details} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {eventsList.length > 0 && (
                <div className="admin-pagination" aria-label="Paginação dos detalhes">
                  <div className="admin-pagination__summary">
                    <span>Página {page} de {totalPages}</span>
                    <strong>
                      {Math.min(eventsList.length, (page - 1) * pageSize + 1)}-{Math.min(eventsList.length, page * pageSize)} de {eventsList.length} atividades
                    </strong>
                  </div>
                  <div className="admin-pagination__nav">
                    <div className="admin-pagination__actions">
                      <button
                        type="button"
                        className="admin-link-button"
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        <ChevronLeftIcon />
                        Anterior
                      </button>
                    </div>
                    <div className="admin-pagination__pages" aria-label="Paginação dos detalhes">
                      {pageNumbers.map((item) => (
                        <button
                          key={item}
                          type="button"
                          className={`admin-pagination__page${item === page ? ' admin-pagination__page--active' : ''}`}
                          aria-current={item === page ? 'page' : undefined}
                          onClick={() => setPage(item)}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                    <div className="admin-pagination__actions admin-pagination__actions--end">
                      <button
                        type="button"
                        className="admin-link-button"
                        disabled={page === totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        Próxima
                        <ChevronRightIcon />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Resumo por colaborador */}
        <div className="admin-panel admin-panel--table-card" style={{ padding: '24px', backgroundColor: 'var(--surface)' }}>
          <div className="admin-panel__header" style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: 0 }}>Resumo de horas por colaborador</h3>
            <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: '0.82rem' }}>
              Horas calculadas por entrada e saída, descontando intervalos registrados.
            </p>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)' }}>
              Calculando jornadas...
            </div>
          ) : workSummaries.length === 0 ? (
            <div className="admin-empty-state" style={{ padding: '28px 0' }}>
              <strong>Nenhum resumo disponível</strong>
              <p>Os filtros atuais não retornaram jornadas com entrada e saída.</p>
            </div>
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table admin-table--details">
                  <thead>
                    <tr>
                      <th style={{ width: '220px' }}>Colaborador</th>
                      <th style={{ width: '110px' }}>Dia</th>
                      <th style={{ width: '110px' }}>Semana</th>
                      <th style={{ width: '110px' }}>Mês</th>
                      <th style={{ width: '110px' }}>Jornadas</th>
                      <th style={{ width: '110px' }}>Espaços</th>
                      <th style={{ width: '150px', textAlign: 'right' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleWorkSummaries.map((summary) => (
                      <tr key={summary.collaboratorName}>
                        <td><strong>{summary.collaboratorName}</strong></td>
                        <td>{formatDuration(summary.dayMinutes)}</td>
                        <td>{formatDuration(summary.weekMinutes)}</td>
                        <td>{formatDuration(summary.monthMinutes)}</td>
                        <td>{summary.journeysCount}</td>
                        <td>{summary.spacesCount}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            className="admin-link-button"
                            onClick={() => {
                              setSelectedWorkSummary(summary)
                              setJourneyPage(1)
                            }}
                          >
                            Detalhar jornada
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="admin-pagination" aria-label="Paginação do resumo de horas">
                <div className="admin-pagination__summary">
                  <span>Página {safeSummaryPage} de {summaryTotalPages}</span>
                  <strong>
                    {Math.min(workSummaries.length, (safeSummaryPage - 1) * pageSize + 1)}-{Math.min(workSummaries.length, safeSummaryPage * pageSize)} de {workSummaries.length} colaboradores
                  </strong>
                </div>
                <div className="admin-pagination__nav">
                  <div className="admin-pagination__actions">
                    <button
                      type="button"
                      className="admin-link-button"
                      disabled={safeSummaryPage === 1}
                      onClick={() => setSummaryPage((current) => Math.max(1, current - 1))}
                    >
                      <ChevronLeftIcon />
                      Anterior
                    </button>
                  </div>
                  <div className="admin-pagination__pages" aria-label="Páginas do resumo de horas">
                    {summaryPageNumbers.map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={`admin-pagination__page${item === safeSummaryPage ? ' admin-pagination__page--active' : ''}`}
                        aria-current={item === safeSummaryPage ? 'page' : undefined}
                        onClick={() => setSummaryPage(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                  <div className="admin-pagination__actions admin-pagination__actions--end">
                    <button
                      type="button"
                      className="admin-link-button"
                      disabled={safeSummaryPage === summaryTotalPages}
                      onClick={() => setSummaryPage((current) => Math.min(summaryTotalPages, current + 1))}
                    >
                      Próxima
                      <ChevronRightIcon />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedWorkSummary ? (
        <div className="admin-modal" role="dialog" aria-modal="true" aria-label={`Detalhar jornada: ${selectedWorkSummary.collaboratorName}`}>
          <div className="admin-modal__surface admin-modal__surface--view" style={{ width: 'min(980px, calc(100vw - 32px))' }}>
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              aria-label="Cancelar"
              onClick={closeWorkSummaryModal}
            >
              <XIcon />
            </button>
            <div className="admin-modal__view" style={{ display: 'grid', gap: '18px', padding: '24px' }}>
              <div className="admin-modal__view-hero" style={{ paddingBottom: '12px', borderBottom: '1px solid var(--line)' }}>
                <p className="admin-modal__eyebrow">Detalhar jornada</p>
                <h3 style={{ margin: 0 }}>{selectedWorkSummary.collaboratorName}</h3>
              </div>

              <div className="admin-stat-grid" style={{ gap: '12px' }}>
                <article className="admin-stat" style={{ padding: '14px' }}>
                  <span>Dia</span>
                  <strong>{formatDuration(selectedWorkSummary.dayMinutes)}</strong>
                </article>
                <article className="admin-stat" style={{ padding: '14px' }}>
                  <span>Semana</span>
                  <strong>{formatDuration(selectedWorkSummary.weekMinutes)}</strong>
                </article>
                <article className="admin-stat" style={{ padding: '14px' }}>
                  <span>Mês</span>
                  <strong>{formatDuration(selectedWorkSummary.monthMinutes)}</strong>
                </article>
                <article className="admin-stat" style={{ padding: '14px' }}>
                  <span>Jornadas</span>
                  <strong>{selectedWorkSummary.journeysCount}</strong>
                </article>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Filtrar por espaço, jornada ou data..."
                  value={journeyFilter}
                  onChange={(e) => {
                    setJourneyFilter(e.target.value)
                    setJourneyPage(1)
                  }}
                  style={{ width: '100%', maxWidth: '320px', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '5px', fontSize: '0.85rem', backgroundColor: 'var(--bg-alt)', color: 'var(--text)' }}
                />
              </div>

              <div className="admin-table-wrap" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                <table className="admin-table admin-table--details">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Jornada</th>
                      <th>Espaço</th>
                      <th>Início</th>
                      <th>Fim</th>
                      <th>Horas</th>
                      <th>Eventos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSelectedJourneys.map((journey) => (
                      <tr key={`${journey.collaboratorName}-${journey.date}-${journey.journeyLabel}-${journey.startedAt}`}>
                        <td>{journey.date}</td>
                        <td>{journey.journeyLabel}</td>
                        <td>{journey.space}</td>
                        <td>{journey.startedAt}</td>
                        <td>{journey.endedAt}</td>
                        <td><strong>{formatDuration(journey.workedMinutes)}</strong></td>
                        <td>
                          <button
                            type="button"
                            className="admin-link-button"
                            onClick={() => setSelectedJourneyEvents(journey)}
                          >
                            Ver eventos
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredSelectedJourneys.length > 0 ? (
                <div className="admin-pagination" aria-label="Paginação das jornadas">
                  <div className="admin-pagination__summary">
                    <span>Página {safeJourneyPage} de {journeyTotalPages}</span>
                    <strong>
                      {Math.min(filteredSelectedJourneys.length, (safeJourneyPage - 1) * pageSize + 1)}-{Math.min(filteredSelectedJourneys.length, safeJourneyPage * pageSize)} de {filteredSelectedJourneys.length} jornadas
                    </strong>
                  </div>
                  <div className="admin-pagination__nav">
                    <div className="admin-pagination__actions">
                      <button
                        type="button"
                        className="admin-link-button"
                        disabled={safeJourneyPage === 1}
                        onClick={() => setJourneyPage((current) => Math.max(1, current - 1))}
                      >
                        <ChevronLeftIcon />
                        Anterior
                      </button>
                    </div>
                    <div className="admin-pagination__pages" aria-label="Páginas das jornadas">
                      {journeyPageNumbers.map((item) => (
                        <button
                          key={item}
                          type="button"
                          className={`admin-pagination__page${item === safeJourneyPage ? ' admin-pagination__page--active' : ''}`}
                          aria-current={item === safeJourneyPage ? 'page' : undefined}
                          onClick={() => setJourneyPage(item)}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                    <div className="admin-pagination__actions admin-pagination__actions--end">
                      <button
                        type="button"
                        className="admin-link-button"
                        disabled={safeJourneyPage === journeyTotalPages}
                        onClick={() => setJourneyPage((current) => Math.min(journeyTotalPages, current + 1))}
                      >
                        Próxima
                        <ChevronRightIcon />
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="admin-modal__actions" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="admin-link-button" onClick={closeWorkSummaryModal}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {selectedJourneyEvents ? (
        <div className="admin-modal admin-modal--stacked" role="dialog" aria-modal="true" aria-label={`Eventos da jornada de ${selectedJourneyEvents.collaboratorName}`}>
          <div className="admin-modal__surface admin-modal__surface--view" style={{ width: 'min(900px, calc(100vw - 32px))' }}>
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              aria-label="Fechar"
              onClick={closeJourneyEventsModal}
            >
              <XIcon />
            </button>
            <div className="admin-modal__view" style={{ display: 'grid', gap: '18px', padding: '24px' }}>
              <div className="admin-modal__view-hero" style={{ paddingBottom: '12px', borderBottom: '1px solid var(--line)' }}>
                <p className="admin-modal__eyebrow">Eventos da jornada</p>
                <h3 style={{ margin: 0 }}>
                  {selectedJourneyEvents.collaboratorName} — {selectedJourneyEvents.date} ({selectedJourneyEvents.journeyLabel})
                </h3>
              </div>

              <div className="admin-table-wrap" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                <table className="admin-table admin-table--details">
                  <thead>
                    <tr>
                      <th className="admin-table__time">Horário</th>
                      <th>Tipo</th>
                      <th>Espaço</th>
                      <th className="admin-table__accuracy">Acurácia</th>
                      <th className="admin-table__method">Método</th>
                      <th className="admin-table__operational-details">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedJourneyEvents.events.map((evt, idx) => (
                      <tr
                        key={`${evt.collaboratorName}-${evt.date}-${evt.time}-${idx}`}
                        style={{
                          backgroundColor: evt.type === 'spoof_attempt' || evt.type === 'delay'
                            ? 'rgba(239, 68, 68, 0.03)'
                            : evt.punctualityStatus === 'within_tolerance'
                              ? 'rgba(249, 115, 22, 0.07)'
                              : undefined,
                          borderLeft: evt.type === 'spoof_attempt' || evt.type === 'delay'
                            ? '3px solid #ef4444'
                            : evt.punctualityStatus === 'within_tolerance'
                              ? '3px solid #f97316'
                              : undefined
                        }}
                      >
                        <td className="admin-table__time"><strong>{evt.time}</strong></td>
                        <td>
                          <span
                            style={{
                              padding: '3px 8px',
                              borderRadius: '12px',
                              fontSize: '0.74rem',
                              fontWeight: 700,
                              display: 'inline-block',
                              ...getEventBadgeStyle(evt.type)
                            }}
                          >
                            {getEventLabel(evt.type)}
                          </span>
                        </td>
                        <td>{evt.space}</td>
                        <td className="admin-table__accuracy">
                          <span style={{ color: (evt.accuracy && parseInt(evt.accuracy) > 20) ? '#d97706' : 'inherit' }}>
                            {evt.accuracy || 'N/A'}
                          </span>
                        </td>
                        <td className="admin-table__method">
                          <TruncatedCell value={evt.method} className="admin-table__truncate--muted" />
                        </td>
                        <td className="admin-table__operational-details" style={{
                          color: evt.type === 'delay' ? '#b91c1c' : evt.punctualityStatus === 'within_tolerance' ? '#c2410c' : undefined,
                          fontWeight: evt.type === 'delay' || evt.punctualityStatus === 'within_tolerance' ? 700 : undefined,
                        }}>
                          <TruncatedCell value={evt.details} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="admin-modal__actions" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="admin-link-button" onClick={closeJourneyEventsModal}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {editingProfile ? (
        <div className="admin-modal admin-modal--stacked" role="dialog" aria-modal="true" aria-label={`Editar perfil: ${editingProfile.name}`}>
          <div className="admin-modal__surface admin-modal__surface--view admin-profile-modal">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              aria-label="Cancelar"
              onClick={closeProfileOverlay}
            >
              <XIcon />
            </button>
            <div className="admin-modal__view admin-profile-modal__body">
              <div className="admin-modal__view-hero admin-profile-modal__hero">
                <p className="admin-modal__eyebrow">Perfil do colaborador</p>
                <div>
                  <h3>{editingProfile.name}</h3>
                  <p>{editingProfile.email}</p>
                </div>
                <span className={`admin-status ${profileForm.status === 'active' ? 'admin-status--active' : 'admin-status--inactive'}`}>
                  {profileForm.status === 'active' ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <section className="admin-view-card admin-view-card--wide admin-profile-modal__card">
                <span>Edição do perfil</span>
                <div className="admin-profile-modal__form">
                  <label>
                    <span>Nome</span>
                    <input
                      type="text"
                      value={profileForm.name}
                      onChange={(event) => setProfileForm((current) => ({ ...current, name: event.target.value }))}
                    />
                  </label>
                  <label>
                    <span>E-mail</span>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(event) => setProfileForm((current) => ({ ...current, email: event.target.value }))}
                    />
                  </label>
                  <label>
                    <span>Status</span>
                    <div className="admin-status-switch" data-state={profileForm.status}>
                      <span className={`admin-status-switch__label${profileForm.status !== 'active' ? ' admin-status-switch__label--active' : ''}`}>
                        Inativo
                      </span>
                      <label className="admin-status-switch__control">
                        <input
                          type="checkbox"
                          checked={profileForm.status === 'active'}
                          onChange={(event) =>
                            setProfileForm((current) => ({
                              ...current,
                              status: event.target.checked ? 'active' : 'inactive',
                            }))
                          }
                          aria-label="Status do colaborador"
                        />
                        <span className="admin-status-switch__track">
                          <span className="admin-status-switch__thumb" />
                        </span>
                      </label>
                      <span className={`admin-status-switch__label${profileForm.status === 'active' ? ' admin-status-switch__label--active' : ''}`}>
                        Ativo
                      </span>
                    </div>
                  </label>
                  <label>
                    <span>Jornadas por dia</span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={profileForm.maxDailyJourneys}
                      onChange={(event) =>
                        setProfileForm((current) => ({
                          ...current,
                          maxDailyJourneys: Math.max(1, Math.floor(Number(event.target.value) || 1)),
                        }))
                      }
                    />
                  </label>
                </div>
                {profileFeedback ? (
                  <p className={`admin-profile-modal__feedback ${profileFeedback === 'Perfil atualizado.' ? 'admin-profile-modal__feedback--success' : 'admin-profile-modal__feedback--error'}`}>
                    {profileFeedback}
                  </p>
                ) : null}
              </section>
              <div className="admin-modal__actions admin-profile-modal__actions">
                <button type="button" className="admin-link-button" onClick={closeProfileOverlay}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="admin-action-button"
                  disabled={profileSaving}
                  onClick={() => void handleSaveCollaboratorProfile()}
                >
                  {profileSaving ? 'Salvando...' : 'Salvar perfil'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  )
}
function getDeviceOsLabel(device: string | undefined): string {
  if (!device) return 'Outro'
  const dev = device.toLowerCase()
  if (dev.includes('android') || dev.includes('pixel') || dev.includes('samsung') || dev.includes('motorola') || dev.includes('xiaomi') || dev.includes('sdk build')) {
    return 'Android'
  }
  if (dev.includes('iphone') || dev.includes('ipad') || dev.includes('ios') || dev.includes('macintosh') || dev.includes('mac os')) {
    return 'iOS'
  }
  if (dev.includes('windows')) {
    return 'Windows'
  }
  if (dev.includes('linux')) {
    return 'Linux'
  }
  if (dev.includes('navegador web') || dev.includes('portal') || dev.includes('web')) {
    return 'Web'
  }
  return device
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function HistoryHeaderIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '20px', height: '20px' }}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '12px', height: '12px', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '12px', height: '12px', marginLeft: '4px', display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}
